import { ApiCoreService } from "./core/api-core.service";

export class UsersService {
  constructor() {
    this.apiService = new ApiCoreService();
  }

  /**
   * Call FusionAuth Server to request an access token to the application (login the user)
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   *
   * @returns { json } return token and clientId
   */
  requestToken(code) {
    return this.apiService
      .requestFusionAuth("POST", "oauth2/token", null, null, code, true)
      .then((response) => {
        return response;
      });
  }

  /**
   * Call FusionAuth Server to reconcile an external JWT from another identity provider
   * More information:
   * https://fusionauth.io/docs/v1/tech/identity-providers/external-jwt/
   * https://fusionauth.io/docs/v1/tech/apis/jwt/#reconcile-a-jwt
   *
   * @returns { json } return access_token, refresh_token and user datas
   */
  reconcileJwtToken(body) {
    return this.apiService
      .requestFusionAuth(
        "POST",
        "api/jwt/reconcile",
        null,
        process.env.VUE_APP_SSO_API_KEY,
        body,
        false
      )
      .then((response) => {
        return response;
      });
  }

  /**
   * Call FusionAuth Server to logout a user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   *
   * @returns { json } return token and clientId
   */
  async logout (clientId) {
    return await this.apiService.requestFusionAuth(
      'GET',
      'oauth2/logout',
      `client_id=${clientId}`,
      clientId,
      null
    )
  }

  /**
   * Call FusionAuth Server to confirm if the JWT is good and not expired
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async validateToken() {
    return await this.apiService.requestFusionAuth(
      "GET",
      "api/jwt/validate",
      null,
      "JWT " + localStorage.getItem("access_token"),
      null
    );
  }

  /**
   * Call FusionAuth Server to get a single user
   * The list of user can only be users from the same group as the user who asked the request
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async getUserById(id) {
    return this.apiService.requestFusionAuth(
      "GET",
      `api/user/${id}`,
      null,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  async getUserByEmail(email) {
    return this.apiService.requestFusionAuth(
      "GET",
      "api/user",
      `email=${email}`,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  async getUserByUsername(username) {
    return this.apiService.requestFusionAuth(
      "GET",
      "api/user",
      `username=${username}`,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  /**
   * Call FusionAuth Server to get a single group
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async getGroupById(id) {
    return this.apiService.requestFusionAuth(
      "GET",
      `api/group/${id}`,
      null,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  /**
   * Call FusionAuth Server to create a user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async createUser(user) {
    return this.apiService.requestFusionAuth(
      "POST",
      "api/user/registration",
      null,
      process.env.VUE_APP_SSO_API_KEY,
      user
    );
  }

  /**
   * Call FusionAuth Server to update a user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async updateUser(user) {
    let response1 = await this.updateRegistration(user);
    if (Object.prototype.hasOwnProperty.call(response1, "registration")) {
      return this.apiService.requestFusionAuth(
        "PUT",
        `api/user/${user.user.id}`,
        null,
        process.env.VUE_APP_SSO_API_KEY,
        user
      );
    } else {
      return false;
    }
  }

  /**
   * Call FusionAuth Server to update a user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async updateUserOnly (user) {
    return this.apiService.requestFusionAuth(
        'PUT',
        `api/user/${user.user.id}`,
        null,
        process.env.VUE_APP_SSO_API_KEY,
        user
    );
  }

  async updateRegistration(user) {
    return this.apiService.requestFusionAuth(
      "PUT",
      `api/user/registration/${user.user.id}`,
      null,
      process.env.VUE_APP_SSO_API_KEY,
      user
    );
  }

  async createRegistration(userId, registrations) {
    return this.apiService.requestFusionAuth(
      "POST",
      `api/user/registration/${userId}`,
      null,
      process.env.VUE_APP_SSO_API_KEY,
      registrations
    );
  }


  async addRegistration (data) {
    return this.apiService.requestFusionAuth('POST', `api/user/registration/${data.userId}`, null, process.env.VUE_APP_SSO_API_KEY, data)
  }

  /**
   * Call FusionAuth Server to delete a user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   * For debug, remove "?hardDelete=true" from query string, it will only disable the user and not hard delete it so you can still
   * see it in the usersList after a refresh
   */
  async deleteUserById(id, hardDelete = true) {
    return this.apiService.requestFusionAuth(
      "DELETE",
      `api/user/${id}`,
      `?hardDelete=${hardDelete}`,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  /**
   * Call FusionAuth Server to get a list of user
   * More information:
   * https://fusionauth.io/docs/v1/tech/apis
   */
  async getUserList(
    groupId,
    usersPerPage,
    startRow,
    sortField,
    sortDirection,
    filters
  ) {
    let queryString = `queryString=(registrations.applicationId:${process.env.VUE_APP_SSO_APP_ID} OR registrations.applicationId:${process.env.VUE_APP_UNIFIED_STOCK_SSO_APP_ID} OR registrations.applicationId:${process.env.VUE_APP_OCTI_STOCK_SSO_APP_ID}) AND memberships.groupId:${groupId}`;

    if (filters) {
      for (var filterName in filters) {
        if (filterName == "store") {
          queryString += ` AND (data.userStore:${encodeURIComponent(
            filters[filterName]
          )} OR data.currentStore:${encodeURIComponent(filters[filterName])})`;
        } else {
          queryString += ` AND ${filterName}:${encodeURIComponent(
            filters[filterName]
          )}`;
        }
      }
    }
    if (usersPerPage) {
      queryString += `&numberOfResults=${usersPerPage}`;
    }
    if (startRow) {
      queryString += `&startRow=${startRow}`;
    }
    if (sortField) {
      queryString += `&sortFields[0].name=${sortField}`;
      if (sortDirection) {
        queryString += `&sortFields[0].order=${sortDirection}`;
      }
    }

    return this.apiService.requestFusionAuth(
      "GET",
      `api/user/search`,
      queryString,
      process.env.VUE_APP_SSO_API_KEY,
      null
    );
  }

  async reactivateUser(id) {
    return this.apiService.requestFusionAuth(
      "PUT",
      `api/user/${id}`,
      "?reactivate=true",
      process.env.VUE_APP_SSO_API_KEY,
    );
  }
}
